/**
 * ============================================================================
 * Pricing 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Pricing（产品工价管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 📦 包含的子资源：
 * 1. ProductPriceConfig（计价配置）- 产品计价配置的CRUD
 * 2. ProductPrice（产品工价）- 工价的CRUD、版本管理、导入导出
 * 3. WageRecord（工资记录）- 工资记录查询、确认、结算
 * 4. Statistics（工资统计）- 多维度工资统计
 * 
 * 📖 使用方式：
 * ```typescript
 * import { createPricingApi } from '@bayes-mes/shared/api/pricing/factory'
 * 
 * const pricingApi = createPricingApi(webAdapter)
 * 
 * // 计价配置
 * const configs = await pricingApi.getConfigList({ page: 1 })
 * await pricingApi.createConfig({ material_id: 1, price_mode: 'PRODUCT', calc_type: 'PIECE' })
 * 
 * // 产品工价
 * const prices = await pricingApi.getPriceList({ config_id: 1 })
 * const currentPrice = await pricingApi.getCurrentPrice({ material_id: 1 })
 * 
 * // 工资记录
 * const wages = await pricingApi.getWageList({ user_id: 1 })
 * await pricingApi.confirmWage(123)
 * 
 * // 工资统计
 * const dashboard = await pricingApi.getDashboardStats({ start_date: '2025-11-01' })
 * const userStats = await pricingApi.getStatsByUser({ start_date: '2025-11-01' })
 * ```
 * 
 * @module api/pricing/factory
 * @generated Manual (AI-assisted)
 * @last-updated 2025-11-30
 * ============================================================================
 */

import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'
import type { ApiResponse, PaginationApiResponse } from '../../types/api'
import type {
  ProductPriceConfig,
  ProductPrice,
  WageRecord,
  WageFinanceLedgerResult,
  ConfigStatistics,
  WageDashboardStats,
  UserWageStats,
  MaterialWageStats,
  OperationWageStats,
  TimeWageStats,
  UserWageDetail,
  PriceImportResult,
  WageSyncCurrentPriceResult,
  CreateProductPriceConfigForm,
  UpdateProductPriceConfigForm,
  CreateProductPriceForm,
  UpdateProductPriceForm,
  BatchCreatePriceForm,
  ImportProductPriceForm,
  BatchWageActionForm,
  SyncWageCurrentPriceForm,
  ProductPriceConfigQueryParams,
  ProductPriceQueryParams,
  WageRecordQueryParams,
  WageStatisticsQueryParams,
  UserWageDetailQueryParams,
  PriceVersionHistoryQueryParams,
  CurrentPriceQueryParams,
  PriceExportQueryParams,
  WageExportQueryParams,
  WageFinanceLedgerQueryParams,
} from '../../types/pricing'

/**
 * 创建 Pricing API 实例
 * 
 * @param client - 请求客户端实例
 * @returns Pricing API 方法集合
 */
export function createPricingApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 计价配置 (ProductPriceConfig)
    // ========================================================================
    
    /**
     * 获取计价配置列表
     * 
     * @param params - 查询参数
     * @returns 分页的配置列表
     */
    getConfigList: (params?: ProductPriceConfigQueryParams) =>
      client.get<PaginationApiResponse<ProductPriceConfig>>(API_ROUTES.pricing.configs, { params }),
    
    /**
     * 获取计价配置详情
     * 
     * @param id - 配置ID
     * @returns 配置详情
     */
    getConfigDetail: (id: number) =>
      client.get<ApiResponse<ProductPriceConfig>>(API_ROUTES.pricing.detail(id)),
    
    /**
     * 创建计价配置
     * 
     * @param data - 创建表单数据
     * @returns 创建的配置
     */
    createConfig: (data: CreateProductPriceConfigForm) =>
      client.post<ApiResponse<ProductPriceConfig>>(API_ROUTES.pricing.configs, data),
    
    /**
     * 更新计价配置
     * 
     * @param id - 配置ID
     * @param data - 更新表单数据
     * @returns 更新后的配置
     */
    updateConfig: (id: number, data: UpdateProductPriceConfigForm) =>
      client.put<ApiResponse<ProductPriceConfig>>(API_ROUTES.pricing.detail(id), data),
    
    /**
     * 删除计价配置
     * 
     * @param id - 配置ID
     */
    deleteConfig: (id: number) =>
      client.delete<ApiResponse<null>>(API_ROUTES.pricing.delete(id)),
    
    /**
     * 启用计价配置
     * 
     * @param id - 配置ID
     * @returns 更新后的配置
     */
    enableConfig: (id: number) =>
      client.put<ApiResponse<ProductPriceConfig>>(API_ROUTES.pricing.update(id), {}),
    
    /**
     * 停用计价配置
     * 
     * @param id - 配置ID
     * @returns 更新后的配置
     */
    disableConfig: (id: number) =>
      client.put<ApiResponse<ProductPriceConfig>>(API_ROUTES.pricing.configsDisable(id), {}),
    
    /**
     * 获取配置版本历史
     * 
     * @param materialId - 产品ID
     * @returns 该产品所有版本的配置列表
     */
    getConfigHistory: (materialId: number) =>
      client.get<ApiResponse<ProductPriceConfig[]>>(API_ROUTES.pricing.configsHistory(materialId)),
    
    /**
     * 获取配置统计信息
     * 
     * @returns 配置统计
     */
    getConfigStatistics: () =>
      client.get<ApiResponse<ConfigStatistics>>(API_ROUTES.pricing.statistics),
    
    /**
     * 获取可配置的产品列表（未配置的成品和半成品）
     * 
     * @returns 可配置的产品列表
     */
    getAvailableMaterials: () =>
      client.get<ApiResponse<Array<{
        id: number
        materialCode: string
        materialName: string
        materialType: string
        specification?: string
      }>>>(API_ROUTES.pricing.availableMaterials),
    
    // ========================================================================
    // 产品工价 (ProductPrice)
    // ========================================================================
    
    /**
     * 获取工价列表
     * 
     * @param params - 查询参数
     * @returns 分页的工价列表
     */
    getPriceList: (params?: ProductPriceQueryParams) =>
      client.get<PaginationApiResponse<ProductPrice>>(API_ROUTES.pricing.prices, { params }),
    
    /**
     * 获取工价详情
     * 
     * @param id - 工价ID
     * @returns 工价详情
     */
    getPriceDetail: (id: number) =>
      client.get<ApiResponse<ProductPrice>>(API_ROUTES.pricing.pricingPrices(id)),
    
    /**
     * 获取配置下的所有工价
     * 
     * @param configId - 配置ID
     * @param currentOnly - 是否只返回当前版本
     * @returns 工价列表
     */
    getPricesByConfig: (configId: number, currentOnly = true) =>
      client.get<ApiResponse<ProductPrice[]>>(API_ROUTES.pricing.pricesByConfig(configId), {
        params: { current_only: currentOnly },
      }),
    
    /**
     * 获取当前有效工价
     * 
     * @param params - 查询参数（material_id必填）
     * @returns 当前有效工价
     */
    getCurrentPrice: (params: CurrentPriceQueryParams) =>
      client.get<ApiResponse<ProductPrice>>(API_ROUTES.pricing.pricesCurrent, { params }),
    
    /**
     * 获取工价版本历史
     * 
     * @param params - 查询参数（material_id必填）
     * @returns 版本历史列表
     */
    getPriceHistory: (params: PriceVersionHistoryQueryParams) =>
      client.get<ApiResponse<ProductPrice[]>>(API_ROUTES.pricing.pricesHistory, { params }),
    
    /**
     * 创建工价
     * 
     * @param data - 创建表单数据
     * @returns 创建的工价
     */
    createPrice: (data: CreateProductPriceForm) =>
      client.post<ApiResponse<ProductPrice>>(API_ROUTES.pricing.prices, data),
    
    /**
     * 批量创建工价
     * 
     * @param data - 批量创建数据
     * @returns 创建的工价列表
     */
    batchCreatePrices: (data: BatchCreatePriceForm) =>
      client.post<ApiResponse<ProductPrice[]>>(API_ROUTES.pricing.batch, data),
    
    /**
     * 更新工价（会创建新版本）
     * 
     * @param id - 工价ID
     * @param data - 更新表单数据
     * @returns 更新后的工价
     */
    updatePrice: (id: number, data: UpdateProductPriceForm) =>
      client.put<ApiResponse<ProductPrice>>(API_ROUTES.pricing.pricingPrices(id), data),
    
    /**
     * 删除工价
     * 
     * @param id - 工价ID
     */
    deletePrice: (id: number) =>
      client.delete<ApiResponse<null>>(API_ROUTES.pricing.pricingPrices(id)),
    
    /**
     * 导入工价
     * 
     * @param data - 导入数据
     * @returns 导入结果
     */
    importPrices: (data: ImportProductPriceForm) =>
      client.post<ApiResponse<PriceImportResult>>(API_ROUTES.pricing.import, data),
    
    /**
     * 导出工价
     * 
     * @param params - 导出参数
     * @returns 工价列表
     */
    exportPrices: (params?: PriceExportQueryParams) =>
      client.get<ApiResponse<ProductPrice[]>>(API_ROUTES.pricing.export, { params }),
    
    // ========================================================================
    // 工资记录 (WageRecord)
    // ========================================================================
    
    /**
     * 获取工资记录列表
     * 
     * @param params - 查询参数
     * @returns 分页的工资记录列表
     */
    getWageList: (params?: WageRecordQueryParams) =>
      client.get<PaginationApiResponse<WageRecord>>(API_ROUTES.pricing.wages, { params }),
    
    /**
     * 获取工资记录详情
     * 
     * @param id - 记录ID
     * @returns 记录详情
     */
    getWageDetail: (id: number) =>
      client.get<ApiResponse<WageRecord>>(API_ROUTES.pricing.pricingWages(id)),
    
    /**
     * 获取员工工资明细
     * 
     * @param userId - 员工ID
     * @param params - 查询参数
     * @returns 员工工资明细
     */
    getUserWageDetail: (userId: number, params?: UserWageDetailQueryParams) =>
      client.get<ApiResponse<UserWageDetail>>(API_ROUTES.pricing.wagesUser(userId), { params }),
    
    /**
     * 确认工资记录
     * 
     * @param id - 记录ID
     * @returns 确认后的记录
     */
    confirmWage: (id: number) =>
      client.post<ApiResponse<WageRecord>>(API_ROUTES.pricing.confirm(id)),
    
    /**
     * 批量确认工资记录
     * 
     * @param data - 要确认的记录ID列表
     * @returns 操作结果
     */
    batchConfirmWages: (data: BatchWageActionForm) =>
      client.post<ApiResponse<{ success: number; failed: number }>>(
        API_ROUTES.pricing.batchConfirm,
        data
      ),
    
    /**
     * 结算工资记录
     * 
     * @param id - 记录ID
     * @returns 结算后的记录
     */
    settleWage: (id: number) =>
      client.post<ApiResponse<WageRecord>>(API_ROUTES.pricing.settle(id)),
    
    /**
     * 批量结算工资记录
     * 
     * @param data - 要结算的记录ID列表
     * @returns 操作结果
     */
    batchSettleWages: (data: BatchWageActionForm) =>
      client.post<ApiResponse<{ success: number; failed: number }>>(
        API_ROUTES.pricing.batchSettle,
        data
      ),
    
    /**
     * 作废工资记录
     * 
     * @param id - 记录ID
     * @returns 作废后的记录
     */
    voidWage: (id: number) =>
      client.post<ApiResponse<WageRecord>>(API_ROUTES.pricing.void(id)),

    /**
     * 按日期范围同步工资到当前实时单价
     *
     * 注意：后端会按照执行同步时的当前实时工价重算，不按报工发生日历史单价回溯。
     *
     * @param data - 同步筛选条件
     * @returns 同步结果
     */
    syncWageCurrentPrices: (data: SyncWageCurrentPriceForm) =>
      client.post<ApiResponse<WageSyncCurrentPriceResult>>(
        API_ROUTES.pricing.wagesSyncCurrentPrices,
        data
      ),
    
    /**
     * 导出工资数据
     * 
     * @param params - 导出参数
     * @returns 工资记录列表
     */
    exportWages: (params?: WageExportQueryParams) =>
      client.get<ApiResponse<WageRecord[]>>(API_ROUTES.pricing.wagesExport, { params }),

    /**
     * 获取财务工资台账
     *
     * @param params - 台账筛选参数
     * @returns 财务可核对的工资汇总、应发明细与复核记录
     */
    getWageFinanceLedger: (params?: WageFinanceLedgerQueryParams) =>
      client.get<ApiResponse<WageFinanceLedgerResult>>(
        API_ROUTES.pricing.wagesFinanceLedger,
        { params }
      ),
    
    // ========================================================================
    // 工资统计 (Statistics)
    // ========================================================================
    
    /**
     * 获取看板统计数据
     * 
     * @param params - 查询参数
     * @returns 看板统计
     */
    getDashboardStats: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<WageDashboardStats>>(API_ROUTES.pricing.dashboard, { params }),
    
    /**
     * 按员工统计工资
     * 
     * @param params - 查询参数
     * @returns 员工工资统计列表
     */
    getStatsByUser: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<UserWageStats[]>>(API_ROUTES.pricing.statisticsByUser, { params }),
    
    /**
     * 按产品统计工资
     * 
     * @param params - 查询参数
     * @returns 产品工资统计列表
     */
    getStatsByMaterial: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<MaterialWageStats[]>>(API_ROUTES.pricing.statisticsByMaterial, { params }),
    
    /**
     * 按工序统计工资
     * 
     * @param params - 查询参数
     * @returns 工序工资统计列表
     */
    getStatsByOperation: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<OperationWageStats[]>>(API_ROUTES.pricing.statisticsByOperation, { params }),
    
    /**
     * 按日统计工资
     * 
     * @param params - 查询参数
     * @returns 日工资统计列表
     */
    getStatsByDay: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<TimeWageStats[]>>(API_ROUTES.pricing.statisticsByDay, { params }),
    
    /**
     * 按周统计工资
     * 
     * @param params - 查询参数
     * @returns 周工资统计列表
     */
    getStatsByWeek: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<TimeWageStats[]>>(API_ROUTES.pricing.statisticsByWeek, { params }),
    
    /**
     * 按月统计工资
     * 
     * @param params - 查询参数
     * @returns 月工资统计列表
     */
    getStatsByMonth: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<TimeWageStats[]>>(API_ROUTES.pricing.statisticsByMonth, { params }),
    
    /**
     * 按年统计工资
     * 
     * @param params - 查询参数
     * @returns 年工资统计列表
     */
    getStatsByYear: (params?: WageStatisticsQueryParams) =>
      client.get<ApiResponse<TimeWageStats[]>>(API_ROUTES.pricing.statisticsByYear, { params }),
  }
}

/**
 * Pricing API 类型
 */
export type PricingApi = ReturnType<typeof createPricingApi>
